import { FRAAutocomplete } from "Components/fra-e-autocomplete/fra-e-autocomplete";
import { find, findAll, onEvent, removeClass, addClass } from "@kluntje/js-utils/lib/dom-helpers";
import { fetchJSON } from "Helper/apiHelper";
import { ACTIVE } from "Helper/cssClasses";

export class FRAAirportsInput extends FRAAutocomplete {
  constructor() {
    super({
      ui: {
        input: ".fra-js-autocomplete__input :-one",
      },
      events: [
        {
          event: "click",
          target: "resetBtn",
          handler: "resetInputValue",
        },
        {
          event: "input",
          target: "input",
          handler: "dispatchInputValue",
        },
        {
          event: "keydown",
          target: "input",
          handler: "handleKeyboardNavigation",
        },
      ],
      initialStates: {
        inputValue: "",
        autosuggesterDropdownIndex: 0,
      },
      reactions: {
        inputValue: ["dispatchInputValue"],
      },
    });
  }

  /**
   * Set the dropdown list index to 0
   */
  resetDropdownIndex(): void {
    this.setState({ autosuggesterDropdownIndex: 0 });
  }

  /**
   * Return the hidden input element
   */
  get hiddenInput(): HTMLInputElement {
    const hiddenInput = find(this, `input[name=${this.ui.input.getAttribute("id")}]`) as HTMLInputElement;
    return hiddenInput;
  }

  /**
   * Move the focus trough the autosuggester list with with keyboard arrows, select an option on enter keypress
   * and close the autosuggester on esc kepress
   * @param {KeyboardEvent} event
   */
  handleKeyboardNavigation(event: KeyboardEvent): void {
    const listItems = findAll(this.ui.autocompleteList, "li");
    const listItemsArray = ["", ...listItems];
    const arrowDown = event.keyCode === 40;
    const arrowUp = event.keyCode === 38;
    const enter = event.keyCode === 13;
    const escape = event.keyCode === 27;
    const stepUp = this.state.autosuggesterDropdownIndex - 1;
    const stepDown = this.state.autosuggesterDropdownIndex + 1;
    const options = {
      optionId: "",
      optionLabel: "",
    };

    const handleOptionsSelection = () => {
      for (let i = 1; i < listItemsArray.length; i++) {
        if (listItemsArray[i]) {
          if (listItemsArray[i] === listItemsArray[this.state.autosuggesterDropdownIndex]) {
            addClass(listItemsArray[i] as HTMLElement, ACTIVE);
            options.optionId = (listItemsArray[i] as HTMLElement).getAttribute("data-keyboard-id")!;
            options.optionLabel = (listItemsArray[i] as HTMLElement).getAttribute("data-keyboard-label")!;
          } else {
            removeClass(listItemsArray[i] as HTMLElement, ACTIVE);
          }
        }
      }
    };

    if (arrowDown) {
      this.setState({ autosuggesterDropdownIndex: stepDown });
      handleOptionsSelection();
    }

    if (arrowUp && this.state.autosuggesterDropdownIndex !== 1) {
      this.setState({ autosuggesterDropdownIndex: stepUp });
      handleOptionsSelection();
    }

    if (enter) {
      handleOptionsSelection();
      this.ui.input.value = options.optionLabel;
      this.setState({ inputValue: options.optionLabel });
      this.ui.input.setAttribute("data-id", options.optionId);
      this.hiddenInput.value = options.optionId;
      this.hideResetBtn();
      this.collapse();
      removeClass(this, ACTIVE);
      this.resetDropdownIndex();
    }

    if (escape) {
      this.resetInput();
      this.resetDropdownIndex();
    }
  }

  afterComponentRender() {
    const initialValue = this.getAttribute("data-value");

    if (initialValue) {
      fetchJSON(this.generateRequest(initialValue))
        .then(response => {
          this.ui.input.value = response[0].label;
          this.setState({ inputValue: response[0].label });
          this.ui.input.setAttribute("data-id", response[0].id);
          this.hiddenInput.value = response[0].id;
        })
        .catch(error => {
          console.warn(`An error occurred while loading the component: ${error}`);
          return "";
        });
    }
  }

  /**
   * Dispatch a custom event with the input value when the input value is updated
   */
  dispatchInputValue(): void {
    this.dispatchEvent(
      new CustomEvent("fra-autocomplete-input-value", {
        detail: {
          value: this.ui.input.value,
        },
      }),
    );
  }

  /**
   * extend resetInput method for updating the state
   */
  resetInputValue(): void {
    this.resetInput();
    this.setState({ inputValue: "" });
  }

  /**
   * Set the input value with the value from the dropdown list that the user clicks.
   */
  handleSuggestionSelection(): void {
    const suggestions = findAll(this, ".fra-js-suggestion-link");

    suggestions.forEach(item => {
      onEvent(
        item,
        "click",
        e => {
          e.preventDefault();
          const suggestion = find(item as HTMLElement, ".fra-e-link__label");
          if (suggestion) {
            this.ui.input.value = suggestion.innerHTML;
            this.setState({ inputValue: suggestion.innerHTML });
            this.ui.input.setAttribute("data-id", suggestion.getAttribute("data-airport-id"));
            this.hiddenInput.value = suggestion.getAttribute("data-airport-id")!;
          }
          removeClass(this, ACTIVE);
        },
        this,
      );
    });
  }

  /**
   * Fetch data from API and return response
   * @param {string} keyword
   */
  async fetchSuggestions(keyword: string) {
    await fetchJSON(this.generateRequest(keyword))
      .then(response => {
        this.renderSuggestions(response);
      })
      .catch(error => {
        console.warn(`An error occurred while loading the component: ${error}`);
        return "";
      });
  }

  /**
   * @override
   * Render suggestions in list
   * @param {Array<Suggestion>} suggestions
   */
  renderSuggestions(suggestions: Array<any>) {
    const autocompleteItems = suggestions.reduce((result, suggestion: { label: string; id: string }) => {
      result += `<li class="fra-e-autocomplete__list-item" data-keyboard-id="${suggestion.id}" data-keyboard-label="${suggestion.label}">
          <a class="fra-e-link fra-e-link--with-icon fra-e-link--animated-right fra-js-suggestion-link"
          href="#${suggestion.id}"
        >
          <span class="fra-e-link__label" data-airport-id="${suggestion.id}">${suggestion.label}</span>
          <fra-e-icon class="fra-e-icon" icon-id="fra-icon-arrow-right"></fra-e-icon>
        </a>
        </li>`;
      return result;
    }, "");

    this.ui.autocompleteList.innerHTML = autocompleteItems;
    this.showAutocompleteList();
    this.handleSuggestionSelection();
  }

  /**
   * Generate request to fetch data from
   * @param {string} keyword
   * @returns {string}
   */
  generateRequest(keyword: string): string {
    const keywordUrl = `/term=${keyword}/result.json`;
    // https://www.frankfurt-airport.com/de/_jcr_content.airport_suggest.json/filterGeoRailway=1/term=ber/result.json
    return `${this.apiUrl}${keywordUrl}`;
  }
}

customElements.define("fra-m-airports-input", FRAAirportsInput);
